import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import {
  FaBars,
  FaTimes,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaIdCard,
  FaWhatsapp,
  FaCopy,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

/* =======================
   ANIMATED BACKGROUND
======================= */

const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

/* =======================
   NAVBAR
======================= */

const TopNav = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(11, 14, 20, 0.85);
  backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const NavInner = styled.div`
  max-width: 1100px;
  margin: auto;
  padding: 14px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Brand = styled(Link)`
  color: #ffb97b;
  font-weight: 700;
  text-decoration: none;
  font-size: 18px;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 22px;

  a {
    color: #cbd5e1;
    text-decoration: none;

    &:hover {
      color: #ffb97b;
    }
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const Burger = styled.div`
  display: none;
  color: #fff;
  font-size: 22px;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

/* =======================
   MOBILE MENU
======================= */

const MobileMenu = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: #0b0e14;
  z-index: 200;
  padding: 90px 30px;
`;

const Close = styled.div`
  position: absolute;
  top: 22px;
  right: 22px;
  font-size: 24px;
  color: #fff;
  cursor: pointer;
`;

const MobileLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 26px;

  a {
    color: #fff;
    font-size: 20px;
    text-decoration: none;
  }
`;

/* =======================
   PAGE
======================= */

const Page = styled.div`
  min-height: 100vh;
  padding: 120px 20px 80px;

  background: linear-gradient(-45deg, #0b0e14, #141a2a, #0f172a, #0b0e14);
  background-size: 400% 400%;
  animation: ${gradientMove} 12s ease infinite;
`;

const Container = styled.div`
  max-width: 1100px;
  margin: auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 50px;

  h1 {
    font-size: 42px;
    color: #ffffff;
  }

  p {
    margin-top: 10px;
    color: #cbd5e1;
    max-width: 700px;
    margin: auto;
    line-height: 1.6;
  }
`;

/* =======================
   GRID + CARD
======================= */

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: rgba(17, 22, 36, 0.75);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 22px;
  display: flex;
  gap: 14px;
  backdrop-filter: blur(12px);
  transition: 0.3s;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(255, 185, 123, 0.5);
  }

  svg {
    color: #ffb97b;
    font-size: 20px;
    margin-top: 4px;
  }
`;

const Title = styled.div`
  font-size: 13px;
  color: #94a3b8;
  margin-bottom: 6px;
`;

const Value = styled.div`
  color: #ffffff;
  font-size: 15px;
  line-height: 1.5;
`;

/* =======================
   BUTTONS
======================= */

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 185, 123, 0.12);
  border: 1px solid rgba(255, 185, 123, 0.3);
  color: #ffb97b;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;

  &:hover {
    background: rgba(255, 185, 123, 0.2);
  }
`;

/* =======================
   WHATSAPP FLOAT BUTTON
======================= */

const WhatsAppButton = styled.a`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: #25d366;
  color: #fff;
  width: 55px;
  height: 55px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 24px;
  z-index: 999;
  box-shadow: 0 10px 25px rgba(0,0,0,0.3);

  &:hover {
    transform: scale(1.1);
  }
`;

/* =======================
   COMPONENT
======================= */

const ContactUs = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);

    axios
      .get("https://apis.rechargejunction.in/v1/meta")
      .then((res) => {
        setMeta(res?.data?.data || null);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied!");
  };

  return (
    <>
      {/* NAV */}
      <TopNav>
        <NavInner>
          <Brand to="/">RechargeJunction</Brand>

          <NavLinks>
            <Link to="/">Home</Link>
            <Link to="/login">Login</Link>
            <Link to="/register">Register</Link>
            <Link to="/contact">Contact</Link>
          </NavLinks>

          <Burger onClick={() => setMenuOpen(true)}>
            <FaBars />
          </Burger>
        </NavInner>
      </TopNav>

      {/* PAGE */}
      <Page>
        <Container>
          <Header>
            <h1>Contact Us</h1>
            <p>
              We’re here for API support, business queries, and partnerships.
            </p>
          </Header>

          {loading ? (
            <p style={{ color: "#cbd5e1", textAlign: "center" }}>Loading...</p>
          ) : (
            <Grid>
              <Card>
                <FaMapMarkerAlt />
                <div>
                  <Title>Company</Title>
                  <Value>{meta?.firmName}</Value>
                  <Value>{meta?.firmAddress}</Value>
                </div>
              </Card>

              <Card>
                <FaEnvelope />
                <div>
                  <Title>Email</Title>
                  <Value>{meta?.appEmailAddress}</Value>
                  <ActionRow>
                    <Button onClick={() => copyToClipboard(meta?.appEmailAddress)}>
                      <FaCopy /> Copy
                    </Button>
                  </ActionRow>
                </div>
              </Card>

              <Card>
                <FaPhoneAlt />
                <div>
                  <Title>Phone</Title>
                  <Value>{meta?.appPhoneNumber}</Value>
                  <ActionRow>
                    <Button onClick={() => copyToClipboard(meta?.appPhoneNumber)}>
                      <FaCopy /> Copy
                    </Button>
                  </ActionRow>
                </div>
              </Card>

              <Card>
                <FaIdCard />
                <div>
                  <Title>GST</Title>
                  <Value>{meta?.gstNumber}</Value>
                </div>
              </Card>
            </Grid>
          )}
        </Container>
      </Page>

      {/* WHATSAPP FLOAT */}
      <WhatsAppButton
        href={`https://wa.me/${meta?.appPhoneNumber || ""}`}
        target="_blank"
      >
        <FaWhatsapp />
      </WhatsAppButton>
    </>
  );
};

export default ContactUs;